import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import type CommonState from '@modules/store/common/types/CommonState.entity';
import api from '@modules/store/landlord/api/index.api';
import {
  LandlordContractsGet,
  LandlordCreate,
  LandlordGet,
  LandlordProfileAddressGet,
  LandlordProfileUpdate,
  LandlordRentalObjectsGet,
  LandlordContractDelete,
  LandlordInspectionReportsGet,
  LandlordInspectionReportDelete,
  LandlordDownloadTokenGet,
  LandlordDownloadSessionGet,
  LandlordTenanciesGet,
  LandlordTenancySubscriptionsGet,
} from '@modules/store/landlord/types/LandlordAPI.interface';
import type LandlordSate from '@modules/store/landlord/types/LandlordState.entity';
import { ActionTree } from 'vuex';

export const actions: ActionTree<LandlordSate, CommonState> = {
  fetchLandlord: ({ commit }, get: LandlordGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlord(get)
      .then((data: any) => {
        commit('setLandlord', data.landlord);
        commit('setLandlordIdSudo', data.isSudo);
        commit('setLandlordFirstRentalObjectId', data.firstRentalObjectId);
        commit('setLandlordLoaded', true);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  fetchLandlordRentalObjects: ({ commit }, get: LandlordRentalObjectsGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlordRentalObjects(get)
      .then((data: any) => {
        commit('setLandlordRentalObjects', data.rentalObjects);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
  fetchLandlordContracts: ({ commit }, get: LandlordContractsGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlordContracts(get)
      .then((data: any) => {
        commit('setLandlordContracts', data.contracts);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
  fetchLandlordInspectionReports: ({ commit }, get: LandlordInspectionReportsGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlordInspectionReports(get)
      .then((data: any) => {
        commit('setLandlordInspectionReports', data.inspectionReports);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
  fetchLandlordTenancies: ({ commit }, get: LandlordTenanciesGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlordTenancies(get)
      .then((data: any) => {
        commit('setLandlordTenancies', data.tenancies);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
  fetchLandlordTenancySubscriptions: ({ commit }, get: LandlordTenancySubscriptionsGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getLandlordTenancySubscriptions(get)
      .then((data: any) => {
        commit('setLandlordTenancySubscriptions', {
          tenancyId: get.tenancyId,
          subscriptions: data.subscriptionPlans,
        });
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  createLandlord: ({ commit }, create: LandlordCreate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .createLandlord(create)
      .then((data: any) => {
        commit('setLandlord', data.landlord);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  deleteLandlordContract: ({ commit }, remove: LandlordContractDelete) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .deleteLandlordContract(remove)
      .then(() => {
        commit('unsetLandlordContract', remove.uuid);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
  deleteLandlordInspectionReport: ({ commit }, remove: LandlordInspectionReportDelete) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .deleteLandlordInspectionReport(remove)
      .then(() => {
        commit('unsetLandlordInspectionReport', remove.uuid);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  /**
   * @param commit
   * @param get LandlordProfileAddressGet
   */
  fetchProfileLandlordPostalAddress: async ({ commit }, get: LandlordProfileAddressGet) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api()
      .getProfileLandlordPostalAddress(get)
      .then((data: any) => {
        commit('setLandlordAddress', data.address);
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  /**
   * @param commit
   * @param update LandlordProfileAddressUpdate
   */
  patchProfileLandlord: async ({ commit }, update: LandlordProfileUpdate) => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    return new api().patchProfileLandlord(update).finally(() => {
      commit('common/staticData/setLoading', false, { root: true });
      commit('setLandlordLoading', false);
    });
  },

  fetchLandlordDownloadToken: ({ commit }, get: LandlordDownloadTokenGet): Promise<void> => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setLandlordApiError', false);
    return new api()
      .getLandlordDownLoadToken(get)
      .then((data: any) => {
        commit('setLandlordDownloadToken', data.token);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setLandlordApiError', true);
        LogOperations.error('API|Landlord|Actions|getLandlordDownLoadToken', error.message);
        throw new Error('fetchLandlordDownloadToken failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },

  fetchLandlordDownloadSession: ({ commit }, get: LandlordDownloadSessionGet): Promise<void> => {
    commit('common/staticData/setLoading', true, { root: true });
    commit('setLandlordLoading', true);
    commit('common/staticData/setApiError', { error: false }, { root: true });
    commit('setLandlordApiError', false);
    return new api()
      .getLandlordDownLoadSession(get)
      .then((data: any) => {
        commit('setLandlordSessionPdf', data.pdf);
      })
      .catch((error: any) => {
        commit('common/staticData/setApiError', { error: true }, { root: true });
        commit('setLandlordApiError', true);
        LogOperations.error('API|Landlord|Actions|getLandlordDownLoadSession', error.message);
        throw new Error('fetchLandlordDownloadSession failed');
      })
      .finally(() => {
        commit('common/staticData/setLoading', false, { root: true });
        commit('setLandlordLoading', false);
      });
  },
};
